/**
 * Constantes e Enums para Cenários e Formas de Pagamento de Cobrança
 * Grupo Emops / SCORPIO ERP
 * 
 * Regra do Banco de Dados (MySQL formas_pagamentos):
 * - ID 1 = DINHEIRO / ESPÉCIE
 * - ID 2 = CARTÃO DE CRÉDITO
 * - ID 3 = CARTÃO DE DÉBITO
 * - ID 4 = BOLETO BANCÁRIO
 * - ID 5 = TRANSFERÊNCIA BANCÁRIA / DEPÓSITO
 * - ID 6 = PIX
 * - ID 11 = CARTÃO RECORRENTE
 */

export const CobrancaCenarioEnum = Object.freeze({
  BOLETO: 'BOLETO',
  EXPIRADO: 'EXPIRADO',
  PIX: 'PIX',
  CARTAO: 'CARTAO',
  DINHEIRO: 'DINHEIRO',
  TRANSFERENCIA: 'TRANSFERENCIA',
  EMPENHO: 'EMPENHO',
  PERMUTA: 'PERMUTA',
  PAGO_OUTRO_CONTRATO: 'PAGO_OUTRO_CONTRATO',
  OUTRO: 'OUTRO'
});

export const FormaPagamentoIdEnum = Object.freeze({
  DINHEIRO: 1,
  CARTAO_RECORRENTE: 2,
  CARTAO_CREDITO: 3,
  BOLETO: 4,
  TRANSFERENCIA: 5,
  PIX: 6,
  EMPENHO: 10,
  CARTAO_DEBITO: 11,
  PERMUTA: 15,
  PAGO_OUTRO_CONTRATO: 16
});

/**
 * Calcula o atraso em dias e se a conta ultrapassou a validade bancária de 60 dias
 * @param {Object} item
 * @returns {{ diasAtraso: number, isVencido: boolean, isExpirado60Dias: boolean }}
 */
export function calcularAtraso(item) {
  if (!item) return { diasAtraso: 0, isVencido: false, isExpirado60Dias: false };
  const dias = item.dias_atraso != null 
    ? Number(item.dias_atraso) 
    : (item.diasVencidos != null ? Number(item.diasVencidos) : 0);
  const tipoCobranca = String(item.tipo_cobranca || '').toUpperCase();
  const isExpirado60Dias = dias > 60 || tipoCobranca === 'EXPIRADO_RENEGOCIACAO';
  const isVencido = dias > 0;
  return { diasAtraso: dias, isVencido, isExpirado60Dias };
}

/**
 * Identifica o meio de pagamento do item com base no backend, ID real do banco ou nome
 * @param {Object} item
 * @returns {string}
 */
export function identificarMeioPagamento(item) {
  if (!item) return CobrancaCenarioEnum.OUTRO;

  // 1. Meio de pagamento explícito retornado pelo backend
  const tipoMeio = String(item.tipo_meio_pagamento || '').toUpperCase();
  if (
    tipoMeio === CobrancaCenarioEnum.BOLETO ||
    tipoMeio === CobrancaCenarioEnum.PIX ||
    tipoMeio === CobrancaCenarioEnum.CARTAO ||
    tipoMeio === CobrancaCenarioEnum.DINHEIRO ||
    tipoMeio === CobrancaCenarioEnum.TRANSFERENCIA ||
    tipoMeio === CobrancaCenarioEnum.EMPENHO ||
    tipoMeio === CobrancaCenarioEnum.PERMUTA ||
    tipoMeio === CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO
  ) {
    return tipoMeio;
  }

  // 2. ID real da tabela formas_pagamentos do banco
  const rawId = item.id_forma_pagamento != null && item.id_forma_pagamento !== ''
    ? item.id_forma_pagamento
    : (item.forma_pagamento != null && !isNaN(Number(item.forma_pagamento)) ? item.forma_pagamento : null);
  const idForma = rawId != null ? Number(rawId) : null;

  if (idForma === FormaPagamentoIdEnum.BOLETO) return CobrancaCenarioEnum.BOLETO;
  if (idForma === FormaPagamentoIdEnum.DINHEIRO) return CobrancaCenarioEnum.DINHEIRO;
  if (idForma === FormaPagamentoIdEnum.PIX) return CobrancaCenarioEnum.PIX;
  if (
    idForma === FormaPagamentoIdEnum.CARTAO_CREDITO || 
    idForma === FormaPagamentoIdEnum.CARTAO_DEBITO || 
    idForma === FormaPagamentoIdEnum.CARTAO_RECORRENTE
  ) {
    return CobrancaCenarioEnum.CARTAO;
  }
  if (idForma === FormaPagamentoIdEnum.TRANSFERENCIA) return CobrancaCenarioEnum.TRANSFERENCIA;
  if (idForma === FormaPagamentoIdEnum.EMPENHO) return CobrancaCenarioEnum.EMPENHO;
  if (idForma === FormaPagamentoIdEnum.PERMUTA) return CobrancaCenarioEnum.PERMUTA;
  if (idForma === FormaPagamentoIdEnum.PAGO_OUTRO_CONTRATO) return CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO;

  // 3. Pelo nome da forma de pagamento vindo do banco/join
  const nome = String(item.forma_pagamento_nome || item.formaPagamento || '').toUpperCase();
  if (nome.includes('BOLETO')) return CobrancaCenarioEnum.BOLETO;
  if (nome.includes('PIX')) return CobrancaCenarioEnum.PIX;
  if (nome.includes('CART') || nome.includes('CRED') || nome.includes('DEB')) return CobrancaCenarioEnum.CARTAO;
  if (nome.includes('TRANSF') || nome.includes('TED') || nome.includes('DOC') || nome.includes('DEP')) return CobrancaCenarioEnum.TRANSFERENCIA;
  if (nome.includes('DINHEIRO') || nome.includes('ESPECIE') || nome.includes('ESPÉCIE')) return CobrancaCenarioEnum.DINHEIRO;
  if (nome.includes('EMPENHO')) return CobrancaCenarioEnum.EMPENHO;
  if (nome.includes('PERMUTA')) return CobrancaCenarioEnum.PERMUTA;
  if (nome.includes('OUTRO CONTRATO')) return CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO;

  // 4. Fallback legado se forma_pagamento for string com nome textual
  if (typeof item.forma_pagamento === 'string' && isNaN(Number(item.forma_pagamento))) {
    const legacyStr = item.forma_pagamento.toUpperCase();
    if (legacyStr.includes('BOLETO')) return CobrancaCenarioEnum.BOLETO;
    if (legacyStr.includes('PIX')) return CobrancaCenarioEnum.PIX;
    if (legacyStr.includes('CART') || legacyStr.includes('CRED') || legacyStr.includes('DEB')) return CobrancaCenarioEnum.CARTAO;
    if (legacyStr.includes('TRANSF') || legacyStr.includes('TED') || legacyStr.includes('DOC')) return CobrancaCenarioEnum.TRANSFERENCIA;
    if (legacyStr.includes('DINHEIRO') || legacyStr.includes('ESPECIE') || legacyStr.includes('ESPÉCIE')) return CobrancaCenarioEnum.DINHEIRO;
    if (legacyStr.includes('EMPENHO')) return CobrancaCenarioEnum.EMPENHO;
    if (legacyStr.includes('PERMUTA')) return CobrancaCenarioEnum.PERMUTA;
    if (legacyStr.includes('OUTRO CONTRATO')) return CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO;
  }

  return CobrancaCenarioEnum.OUTRO;
}

/**
 * Determina o cenário de cobrança final aplicando a regra de expiração bancária de boletos (> 60 dias)
 * @param {Object} item
 * @returns {string} CobrancaCenarioEnum
 */
export function determinarCenarioCobranca(item) {
  if (!item) return CobrancaCenarioEnum.OUTRO;
  const { isExpirado60Dias } = calcularAtraso(item);
  const meio = identificarMeioPagamento(item);

  if (meio === CobrancaCenarioEnum.BOLETO) {
    return isExpirado60Dias ? CobrancaCenarioEnum.EXPIRADO : CobrancaCenarioEnum.BOLETO;
  }
  if (meio === CobrancaCenarioEnum.PIX) return CobrancaCenarioEnum.PIX;
  if (meio === CobrancaCenarioEnum.CARTAO) return CobrancaCenarioEnum.CARTAO;
  if (meio === CobrancaCenarioEnum.DINHEIRO) return CobrancaCenarioEnum.DINHEIRO;
  if (meio === CobrancaCenarioEnum.TRANSFERENCIA) return CobrancaCenarioEnum.TRANSFERENCIA;
  if (meio === CobrancaCenarioEnum.EMPENHO) return CobrancaCenarioEnum.EMPENHO;
  if (meio === CobrancaCenarioEnum.PERMUTA) return CobrancaCenarioEnum.PERMUTA;
  if (meio === CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO) return CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO;

  if (isExpirado60Dias) return CobrancaCenarioEnum.EXPIRADO;
  return CobrancaCenarioEnum.OUTRO;
}

/**
 * Retorna as propriedades visuais do badge para exibição na listagem de cobrança
 * @param {Object} item
 * @returns {{ text: string, icon: string, class: string, title: string }}
 */
export function getFormaPagamentoBadge(item) {
  if (!item) {
    return { text: 'Boleto', icon: 'barcode', class: 'badge-apple-boleto', title: 'Boleto Bancário' };
  }
  const cenario = determinarCenarioCobranca(item);

  switch (cenario) {
    case CobrancaCenarioEnum.EXPIRADO:
      return {
        text: 'Renegociação (>60d)',
        icon: 'alert',
        class: 'badge-apple-renegociacao',
        title: '⚠️ Boleto vencido há mais de 60 dias (expirado no banco). O disparo orienta o cliente com a chave PIX para negociação com juros e multa.'
      };
    case CobrancaCenarioEnum.BOLETO:
      return {
        text: 'Boleto',
        icon: 'barcode',
        class: 'badge-apple-boleto',
        title: 'Forma de Pagamento: Boleto Bancário ativo (≤ 60 dias). O link dinâmico do boleto registrado é enviado na mensagem.'
      };
    case CobrancaCenarioEnum.PIX:
      return {
        text: 'PIX',
        icon: 'pix',
        class: 'badge-apple-pix',
        title: 'Forma de Pagamento: PIX. Mensagem informa os dados e a chave CNPJ da empresa para pagamento instantâneo.'
      };
    case CobrancaCenarioEnum.CARTAO:
      return {
        text: 'Cartão',
        icon: 'card',
        class: 'badge-apple-cartao',
        title: 'Forma de Pagamento: Cartão de Crédito/Débito. Mensagem lembra o cliente do vencimento e solicita a confirmação do pagamento.'
      };
    case CobrancaCenarioEnum.DINHEIRO:
      return {
        text: 'Dinheiro',
        icon: 'cash',
        class: 'badge-apple-dinheiro',
        title: 'Forma de Pagamento: Dinheiro / Espécie. Mensagem orienta o acerto em dinheiro diretamente com o setor financeiro.'
      };
    case CobrancaCenarioEnum.TRANSFERENCIA:
      return {
        text: 'Transferência',
        icon: 'bank',
        class: 'badge-apple-transferencia',
        title: 'Forma de Pagamento: Transferência Bancária / TED / DOC.'
      };
    case CobrancaCenarioEnum.EMPENHO:
      return {
        text: 'Empenho',
        icon: 'bank',
        class: 'badge-apple-empenho',
        title: 'Forma de Pagamento: Empenho (Órgão Público / Governamental). Cobrança informativa.'
      };
    case CobrancaCenarioEnum.PERMUTA:
      return {
        text: 'Permuta',
        icon: 'sync',
        class: 'badge-apple-permuta',
        title: 'Forma de Pagamento: Permuta de Serviços/Produtos conforme acordado em contrato.'
      };
    case CobrancaCenarioEnum.PAGO_OUTRO_CONTRATO:
      return {
        text: 'Outro Contrato',
        icon: 'file',
        class: 'badge-apple-outro-contrato',
        title: 'Forma de Pagamento: Faturamento vinculado e quitado em outro contrato.'
      };
    default: {
      const nomeReal = item.forma_pagamento_nome || item.formaPagamento || 'Outro';
      const idInfo = item.id_forma_pagamento ? ` (ID: ${item.id_forma_pagamento})` : '';
      return {
        text: nomeReal,
        icon: 'file',
        class: 'badge-apple-outro',
        title: `Forma de Pagamento: ${nomeReal}${idInfo}.`
      };
    }
  }
}

/**
 * Retorna o ícone e a cor visual correspondente ao nome da forma de pagamento para selects e cabeçalhos
 * @param {string} nomeForma
 * @returns {{ icon: string, color: string }}
 */
export function getFormaPagamentoMeta(nomeForma) {
  const nome = String(nomeForma || '').toUpperCase();
  if (nome.includes('BOLETO')) {
    return { icon: 'barcode', color: '#0284c7' };
  }
  if (nome.includes('PIX')) {
    return { icon: 'pix', color: '#16a34a' };
  }
  if (
    nome.includes('CARTAO') || nome.includes('CARTÃO') || 
    nome.includes('CREDITO') || nome.includes('CRÉDITO') || 
    nome.includes('DEBITO') || nome.includes('DÉBITO')
  ) {
    return { icon: 'card', color: '#7e22ce' };
  }
  if (nome.includes('DINHEIRO') || nome.includes('ESPECIE') || nome.includes('ESPÉCIE')) {
    return { icon: 'cash', color: '#047857' };
  }
  if (
    nome.includes('DEPOSITO') || nome.includes('DEPÓSITO') || 
    nome.includes('TRANSF') || nome.includes('TED') || 
    nome.includes('DOC') || nome.includes('BANCARIO') || nome.includes('BANCÁRIO')
  ) {
    return { icon: 'bank', color: '#4338ca' };
  }
  if (nome.includes('RECORRENTE') || nome.includes('PAGSEGURO') || nome.includes('PERMUTA')) {
    return { icon: 'sync', color: '#d97706' };
  }
  if (nome.includes('EMPENHO')) {
    return { icon: 'bank', color: '#0891b2' };
  }
  if (nome.includes('OUTRO CONTRATO')) {
    return { icon: 'file', color: '#6d28d9' };
  }
  return { icon: 'default', color: '#64748b' };
}
